import { ESCALA, PALETA_SERIES, formatarValor } from "../tema";
import { estiloRotulo, opcoesBase } from "./base";

/**
 * Família 5 — série histórica longa com várias linhas.
 *
 * Comparação de vários anos no mesmo eixo (ex.: Soja 2023 a 2026), com
 * dezenas ou centenas de pontos por linha. Aqui a regra dos rótulos muda:
 * rotular tudo seria ilegível, então só os pontos escolhidos recebem
 * caixa — por padrão as pontas de cada série.
 *
 * A legenda usa traço em vez de quadrado, que é como as referências
 * distinguem este modelo dos de coluna.
 */
const modelo = {
  id: "linha-historica",
  label: "Linhas (série histórica)",
  descricao: "Várias linhas longas comparadas. Ex.: preço médio de 2023 a 2026.",

  build(spec) {
    const base = opcoesBase(spec);
    const opcoesValor = spec.valores || {};
    const marcados = Array.isArray(opcoesValor.exibirEm) ? opcoesValor.exibirEm : null;

    return {
      ...base,
      chart: { ...base.chart, type: "line", marginBottom: 8 * ESCALA },
      xAxis: {
        ...base.xAxis,
        categories: spec.dados?.categorias || [],
        // Centenas de categorias não cabem em 604px; quem dá a referência
        // temporal são os rótulos dos pontos extremos.
        labels: { enabled: false },
        lineWidth: 0,
      },
      yAxis: { ...base.yAxis, maxPadding: 0.14, minPadding: 0.14 },
      legend: {
        ...base.legend,
        symbolWidth: 16 * ESCALA,
        symbolHeight: 3 * ESCALA,
        symbolRadius: 0,
      },
      plotOptions: {
        ...base.plotOptions,
        line: {
          lineWidth: 2 * ESCALA,
          marker: { enabled: false },
          dataLabels: {
            ...estiloRotulo(spec),
            formatter() {
              return formatarValor(this.y, opcoesValor);
            },
          },
        },
      },
      series: (spec.dados?.series || []).map((serie, i) => {
        const padrao = PALETA_SERIES[i % PALETA_SERIES.length];
        const valores = serie.valores || [];
        return {
          name: serie.nome,
          // Linha sempre em cor sólida: gradiente em traço de 4px vira sujeira.
          color: serie.cor || padrao.cor,
          data: valores.map((valor, j) => ({
            y: valor === null ? null : Number(valor),
            dataLabels: {
              enabled: marcados
                ? marcados.includes(j)
                : opcoesValor.exibirEm === "extremos"
                ? j === 0 || j === valores.length - 1
                : false,
              // Centrada no ponto, a caixa das pontas ficava com metade
              // fora da imagem — "130,00" saía impresso como "30,00".
              // Ancorar a primeira à direita do ponto e a última à
              // esquerda mantém as duas inteiras dentro do quadro.
              align: j === 0 ? "left" : j === valores.length - 1 ? "right" : "center",
            },
          })),
        };
      }),
    };
  },

  exemplo() {
    // Séries sintéticas com a forma das curvas da referência — o objetivo
    // do modelo pronto é o estilo; os números o usuário troca.
    const onda = (base, amp, n, fase) =>
      Array.from({ length: n }, (_, i) =>
        Number((base + amp * Math.sin((i / n) * Math.PI * 2 + fase) + (i % 7) * 0.2).toFixed(2))
      );

    return {
      modelo: "linha-historica",
      titulo: [
        { texto: "Soja", cor: "#F0F000" },
        { texto: " - Média de Preço Brasil ", cor: "#FFFFFF" },
        { texto: "(R$/sc)", cor: "#FFFFFF", tamanho: 8 },
      ],
      subtitulo: [],
      fonte: "Fonte:  IPAR RURAL BUSINESS",
      fundo: "#000000",
      fundoPlot: "nenhum",
      dados: {
        categorias: Array.from({ length: 48 }, (_, i) => `m${i + 1}`),
        series: [
          { nome: "2023", cor: "#3FA9F5", valores: onda(130, 12, 48, 0) },
          { nome: "2024", cor: "#FF4D4F", valores: onda(115, 10, 48, 1.2) },
          { nome: "2025", cor: "#FFFFFF", valores: onda(122, 9, 48, 2.4) },
          { nome: "2026", cor: "#7DD957", valores: onda(140, 11, 48, 3.6) },
        ],
      },
      legenda: { visivel: true },
      valores: { decimais: 2, prefixo: "", sufixo: "", exibirEm: "extremos" },
      rotulo: { corTexto: "#000000", corFundo: "#FFFFFF", corBorda: "#000000" },
      extras: {},
    };
  },
};

export default modelo;
